import { flow, makeAutoObservable } from 'mobx';
import { IHttp, IResponse as IHttpResponse } from '@tolkam/lib-http';

class Transport {

    public pristine: boolean = true;

    public status: null | 'pending'| 'success' | 'error' = null;

    public progress: null | number = null;

    public response: null | IResponse = null;

    private prevCancelFn: any;

    /**
     * @param http IHttp
     */
    public constructor(private http: IHttp) {
        makeAutoObservable(this);
    }

    /**
     * Gets pending status
     */
    get pending(): boolean {
        return this.status === 'pending';
    }

    /**
     * Gets success status
     */
    get success(): boolean {
        const response = this.response;
        return this.status === 'success' && response !== null && response.meta.status.code === 200;
    }

    /**
     * Gets response error message
     */
    get error(): string|null {
        const response = this.response;
        const error = response && response.meta.error;

        return error ? (error.text || error.code) : null;
    }

    /**
     * Gets response messages
     */
    get messages(): {[name: string]: string[]} | null {
        const response = this.response;
        const messages = response && response.meta.messages;
        if(messages) {
            const m: any = {};
            for(let name in messages) {
                m[name] = [messages[name].text];
            }
            return m;
        }
        return null;
    }

    /**
     * Gets payload
     */
    get payload() {
        return this.response?.payload || null;
    }

    /**
     * GET request
     *
     * @param url
     * @param params
     */
    public get(url: string, params?: any) {
        return this.requestFactory({method: 'GET', url, params});
    }

    /**
     * POST request
     *
     * @param url
     * @param data
     * @param params
     */
    public post(url: string, data?: any, params?: any) {
        return this.requestFactory({method: 'POST', url, data, params});
    }

    /**
     * PUT request
     *
     * @param url
     * @param data
     * @param params
     */
    public put(url: string, data?: any, params?: any) {
        return this.requestFactory({method: 'PUT', url, data, params});
    }

    /**
     * DELETE request
     *
     * @param url
     * @param params
     */
    public delete(url: string, params?: any) {
        return this.requestFactory({method: 'DELETE', url, params});
    }

    /**
     * @param requestConfig IRequestConfig
     */
    private requestFactory(requestConfig: IRequestConfig) {
        const that = this;
        const {http, update, prevCancelFn} = that;

        return flow(function*(onResult?: TResultCallback) {
            try {
                const [promise, cancelFn] = http.request(requestConfig, that.setProgress);

                // cancel previous pending request
                prevCancelFn && prevCancelFn();
                that.prevCancelFn = cancelFn;

                update('pending');
                const result: IHttpResponse = (yield promise);

                if(isValidResponse(result.data)) {
                    update('success', result.data);
                } else {
                    update('error');
                    throwAsyncError(makeResponseError(result.request?.responseURL));
                }

            } catch (error) {

                // cancel request if error occurred in try block
                // after request executed
                prevCancelFn && prevCancelFn();

                // throw unknown errors to global error handler
                if(!http.isOwnError(error)) {
                    throwAsyncError(error);
                }

                let apiResponse = error.response?.data;
                if(isValidResponse(apiResponse)) {
                    update('error', apiResponse);
                } else {
                    update('error');
                    throwAsyncError(makeResponseError(error.request.responseURL));
                }

            } finally {
                // make sure onResult() errors are handled too
                try {
                    onResult && onResult(that as any);
                } catch (e) {
                    throwAsyncError(e);
                }
            }

            return that;
        });
    }

    private update = (
        status: Transport['status'],
        response: Transport['response'] = null
    ) => {
        const that = this;
        const progress = status === 'success' ? 100 : 0;

        that.pristine = !that.response && !response;
        that.status = status;
        that.progress = progress;
        that.response = response;
    }

    private setProgress = (progress: number) => {
        this.progress = progress;
    }
}

/**
 * Throws async errors
 *
 * @param error
 */
function throwAsyncError(error: unknown) {
    setTimeout(() => { throw error });
}

/**
 * Validates API response schema
 *
 * @param value
 */
function isValidResponse(value: any) {
    // simple validation for now
    return typeof value === 'object' && 'meta' in value;
}

/**
 * Makes common api response error
 * @param url
 */
function makeResponseError(url?: string) {
    return new Error(`Malformed API response at "${url}"`);
}


type TMetaMessage = {
    code: string;
    text?: string;
};

// interface IPagination {
//     previous: null | string;
//     current: null | string;
//     next: null | string;
// }

interface IResponseMeta {
    status: { code: number; text?: string; };
    error?: { code: string; text?: string; };
    messages?: { [k: string]: TMetaMessage; };
}

interface IResponse {
    payload: null | string | number | {[k:string]: any};
    meta: IResponseMeta;
    // pagination?: IPagination;
}

interface IRequestConfig {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';

    url: string;

    // post data
    data?: {};

    // query params
    params?: {};
}

type TResultCallback = (that: Transport) => void;

interface ITransport extends Transport {}

export default Transport;
export {ITransport}
